import { useEffect, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { useNavigate } from "react-router";
import { getEmailHref } from "js/formatting";
import { Trans, useTranslation } from "react-i18next";

import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import FACStepOne from "components/ServicesAndClaims/Claims/FileAClaim/FACStepOne";
import FACStepTwo from "components/ServicesAndClaims/Claims/FileAClaim/FACStepTwo";
import FACStepThree from "components/ServicesAndClaims/Claims/FileAClaim/FACStepThree";
import FACStepFour from "components/ServicesAndClaims/Claims/FileAClaim/FACStepFour";
import FACStepSix from "components/ServicesAndClaims/Claims/FileAClaim/FACStepSix";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import config from "config/config";
import FACStepFive from "components/ServicesAndClaims/Claims/FileAClaim/FACStepFive";
import FileAClaimContactUs from "components/General/FileAClaimContactUs";

export default function FileAClaimAnnuity() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedPolicy, setSelectedPolicy] = useState({});

  const [formStep, setFormStep] = useState(1);
  const [FACFormData, setFACFormData] = useState({});
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [confirmation, setConfirmation] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [message, setMessage] = useState();
  const [title, setTitle] = useState();
  const [loggedUser, setLoggedUser] = useState();

  // if error happens, when user clicks out of the dialog, show last step for resubmit
  useEffect(() => {
    if (!openDialog && formStep === 6) setFormStep(5);
  }, [openDialog]);

  useBreadcrumb([
    // Customer BC
    { name: "Home", url: "/policies" },
    { name: "Claims", url: "/services-claims/claims" },
    { name: "File a claim", url: "#" },
  ]);

  return (
    <>
      <div className="container mb-8">
        <h1>{t("FNOL-title")}</h1>
        <p>{t("FNOL-description")}</p>
        <hr />
      </div>

      <div className="container ui-component-spacing">
        <div className="grid gap-4 grid-cols-1 lg:gap-6 lg:grid-cols-[66.66667%,33.33333%]">
          <div>
            <div className="grid gap-4 grid-cols-1 lg:gap-6">
              {/* intro card */}
              <div className="ui-card ui-rte-component p-4 xl:p-6">
                <h2 className="text-xl font-body font-bold">
                  {t("FNOL-cardTitle")}
                </h2>

                <hr className="mt-4 mb-6" />

                <div className="text-sm">
                  <p className="font-semibold">{t("FNOL-cardSub-Title")}</p>

                  <ul className="mt-4 list-disc list-outside ml-8">
                    <li>{t("FNOL-card-List-1")}</li>
                    <li>{t("FNOL-card-List-2")}</li>
                    <li>{t("FNOL-card-List-3")}</li>
                    <li>{t("FNOL-card-List-4")}</li>
                    <li>{t("FNOL-cardList-5")}</li>
                    <li>{t("FNOL-cardList-6")}</li>
                    <li>{t("FNOL-card-List-7")}</li>
                  </ul>
                </div>
              </div>

              <div>
                {/* step 1 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-6 mb-4">
                      <div className="inline-flex justify-center items-center h-8 w-8 text-white bg-ui-blue-dark rounded-full overflow-hidden">
                        1
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>

                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-step-Title1")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 1
                        </div>
                      </div>
                      <p>{t("FNOL-stepSub-Title1")}</p>
                      <FACStepOne
                        selectedPolicy={selectedPolicy}
                        setSelectedPolicy={setSelectedPolicy}
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                        setLoggedUser={setLoggedUser}
                      />
                    </div>
                  </div>
                </div>

                {/* step 2 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 1
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        2
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-stepTitle2")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 2
                        </div>
                      </div>

                      <FACStepTwo
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                        loggedUser={loggedUser}
                      />
                    </div>
                  </div>
                </div>

                {/* step 3 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 2
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        3
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-step-Title7")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 3
                        </div>
                      </div>

                      <FACStepThree
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                      />
                    </div>
                  </div>
                </div>

                {/* step 4 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 3
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        4
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-stepTitle3")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 4
                        </div>
                      </div>

                      <FACStepFour
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                      />
                    </div>
                  </div>
                </div>

                {/* step 5 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 4
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        5
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-ui-gray-medium-light mx-auto overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-stepTitle6")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 5
                        </div>
                      </div>

                      <FACStepFive
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                      />
                    </div>
                  </div>
                </div>
                {/* step 6 card */}
                <div className="flex flex-row">
                  <div className="w-8 mr-2 md:flex md:flex-col md:justify-center md:mr-4 lg:mr-8">
                    <div className="flex justify-center items-center mt-3 mb-0">
                      <div
                        className={`inline-flex justify-center items-center h-8 w-8 text-white rounded-full overflow-hidden
                        ${
                          formStep > 5
                            ? "bg-ui-blue-dark"
                            : "bg-ui-gray-medium-dark"
                        }
                        `}
                      >
                        6
                      </div>
                    </div>
                    <div className="flex h-full w-px bg-transparent overflow-hidden"></div>
                  </div>
                  <div className="w-full flex-grow">
                    <div className="ui-card relative px-4 py-4 mb-4 xl:mb-6 xl:px-6">
                      <div className="flex justify-between items-start">
                        <h2 className="flex flex-grow font-body font-bold text-xl mb-0">
                          {t("FNOL-stepTitle4")}
                        </h2>
                        <div className="font-semibold text-sm whitespace-nowrap">
                          Step 6
                        </div>
                      </div>
                      <FACStepSix
                        formStep={formStep}
                        setFormStep={setFormStep}
                        setFACFormData={setFACFormData}
                        setConfirmation={setConfirmation}
                        setIsConfirmationDialogOpen={
                          setIsConfirmationDialogOpen
                        }
                        setOpenDialog={setOpenDialog}
                        setTitle={setTitle}
                        setMessage={setMessage}
                        FACFormData={FACFormData}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <FileAClaimContactUs />
        </div>
      </div>
      {/* confirmation modal */}
      <Transition appear show={isConfirmationDialogOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-10 overflow-y-auto"
          onClose={() => null}
        >
          <DialogBackdrop />
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-2xl p-6 my-32 overflow-hidden text-center align-middle transition-all transform bg-white shadow-xl rounded-lg">
                <Dialog.Title
                  as="h2"
                  className="mx-auto pt-20 pb-8 dialog_label2 font-body text-4xl font-extrabold tracking-tighter text-center"
                >
                  {t("FNOL-confirmationModalTitle")}
                </Dialog.Title>

                <div className="mx-auto lg:w-4/5">
                  <p id="ID_dialogPaymentThankDesc">
                    <strong>{t("FNOL-confirmationModalSubTitle")}</strong>
                  </p>
                  <div className="w-full text-2xl font-bold tracking-tight text-theme-action my-2 text-center xl:text-3xl xl:my-4">
                    {confirmation}
                  </div>
                  <hr />
                  <div className="w-full text-lg font-bold tracking-tight text-black my-2 text-center xl:text-xl xl:my-4">
                    {t("FNOL-confirmationModalStep")}
                  </div>
                  <div className="text-left font-normal tracking-normal px-4">
                    <div className="w-full text-lg font-bold tracking-tight mt-2 text-black my-2 xl:text-xl xl:mb-2 xl:mt-8">
                      {t("FNOL-confirmationModalStepTitle1")}
                    </div>
                    <p className="text-lg font-semibold max-w-sm">
                      <Trans
                        i18nKey="FNOL-confirmationModalStepDescription1"
                        values={{ email: config.CUSTOMER_SERVICE_EMAIL }}
                        components={{
                          break: <br />,
                        }}
                      />
                    </p>
                    <div className="w-full text-lg font-bold tracking-tight mt-2 text-black my-2 xl:text-xl xl:mb-2 xl:mt-8">
                      {t("FNOL-confirmationModalStepTitle2")}
                    </div>
                    <p className="text-lg font-semibold max-w-md">
                      {t("FNOL-confirmationModalStepDescription2")}
                    </p>
                  </div>
                </div>
                <div className="block text-center mt-12 mb-8 ">
                  <button
                    className="ui-btn-primary min-w-[140px] text-base font-semibold mt-4 py-4 px-8"
                    aria-label="Track claim now"
                    onClick={() => {
                      navigate("/policies");
                      setIsConfirmationDialogOpen(false);
                    }}
                  >
                    {t("FNOL-confirmationModalButton2")}
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
      <GeneralPurposeDialog
        state={openDialog}
        setState={setOpenDialog}
        title={title}
        desc={message}
      />
    </>
  );
}
